import './App.css';
import { useEffect, useMemo, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import LinearProgress from '@mui/material/LinearProgress';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import { HashRouter, Routes, Route, Link as RouterLink, Navigate } from "react-router-dom";
import {
  useRecoilState,
} from 'recoil';

import { 
  filterBarInternalValuesDefaultAtom,
  filterBarInternalValuesAtom,
  loadDateAtom,
  addressFilterAtom,
} from './state';

import {
  getParameterFromHash,
} from './util'

import FilterBar from './FilterBar.js'

import DateRange from './filters/DateRange.js'

import GameModePlayed from './analyses/GameModePlayed.js'
import PlayerAward from './analyses/PlayerAward.js'
import PlayerAwardName from './analyses/PlayerAwardName.js'
import PlayerCorrelation from './analyses/PlayerCorrelation.js'
import PlayerIntakeLost from './analyses/PlayerIntakeLost.tsx'
import PlayerResign from './analyses/PlayerResign.tsx'

const instance = new Worker(new URL('./loader.worker.js', import.meta.url));

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

function App() {

  const [loadDate, setLoadDate] = useRecoilState(loadDateAtom);
  const [addressFilter, setAddressFilter] = useRecoilState(addressFilterAtom);

  const [filterBarInternalValuesDefault,setFilterBarInternalValuesDefault] = useRecoilState(filterBarInternalValuesDefaultAtom);
  const [filterBarInternalValues,setFilterBarInternalValues] = useRecoilState(filterBarInternalValuesAtom);
  
  // State about incremental data loading
  const [incrementalData, setIncrementalData] = useState(null);
  const [enoughDataLoaded, setEnoughDataLoaded] = useState(false);

  const [drawerState, setDrawerState] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSettingsOpen, setModalSettingsOpen] = useState(false);
  const [modalOpenGamesModesPlayed, setModalOpenGamesModesPlayed] = useState(false);
  const [modalWarningAllOpen, setModalWarningAllOpen] = useState(false);

  // State for the setting
  const [changedDateRange, setChangedDateRange] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setHours(0,0,0,0) - 3*30*24*3600*1000));
  const [endDate, setEndDate] = useState(new Date(new Date().setHours(0,0,0,0)));
 
  // The state of what the various pages show graphically (after clicking on
  // filterBar update). Before the first update the filterBarInternalValuesDefault
  // values will be used
  const [filterBarValues, setFilterBarValues] = useState([filterBarInternalValuesDefault]);

  const [loadingMessage, setLoadingMessage] = useState("Loading data (data will be cached for future uses):");

  useMemo(() => {
      instance.onmessage = (message) => {
        if (message) {
          if ('battle_player' in message.data) {
            console.log("INFO: message from worker: battle_player.length="+message.data['battle_player']['battle_id'].length);
            console.log("INFO: message from worker: battles.length="+message.data['battles']['battle_id'].length);
            setIncrementalData(message.data);
            if (message.data['battles']['battle_id'].length>1000 || message.data.incrementalDataPercentage===100) {
              setEnoughDataLoaded(true);
            }
          }
          if ('error' in message.data) {
            setLoadingMessage(message.data['error']);
          }
        }
      }
    }
  , []);

  useEffect(() => {
    console.log("INFO: process env "+JSON.stringify(process.env));

    let url = new URL(window.location);
    let idx_q = window.location.href.indexOf("?");
    let query = "";
    if (idx_q!=-1) {
      query = window.location.href.slice(idx_q+1);
    }
    setAddressFilter(query);

    let loadDateStart = parseInt(getParameterFromHash(url.hash, "loadDateStart", new Date(new Date().valueOf() - 3*30*24*3600*1000).valueOf()));
    let loadDateEnd = parseInt(getParameterFromHash(url.hash, "loadDateEnd", new Date().valueOf()));
    let startDate = parseInt(getParameterFromHash(url.hash, "startDate", new Date(new Date().valueOf() - 3*30*24*3600*1000).valueOf()));
    let endDate = parseInt(getParameterFromHash(url.hash, "endDate", new Date().valueOf()));

    // In case there is no startDate set but there is a loadDateStart, use that
    if (!getParameterFromHash(url.hash, "startDate")) {
      startDate = loadDateStart;
    }

    if (loadDateStart<1262300400000) {
      console.log("WARNING: too old date set as load date start, forcing to 2010.");
      loadDateStart = 1262300400000;
    };

    if (startDate<1262300400000) {
      console.log("WARNING: too old date set as date start, forcing to 2010.");
      startDate = 1262300400000;
    };

    if (loadDateEnd>(new Date())) {
      console.log("WARNING: newer date than today set as load date end, forcing to today.");
      loadDateEnd = (new Date()).valueOf();
    };

    if (endDate>(new Date())) {
      console.log("WARNING: newer date than today set as load date end, forcing to today.");
      endDate = (new Date()).valueOf();
    };

    if (loadDateEnd < loadDateStart) {
      console.log("WARNING: for load end date smaller than start date, no date will be used!");
      loadDateStart = loadDateEnd - 1;
    };

    if (endDate < startDate) {
      console.log("WARNING: for load end date smaller than start date, no date will be used!");
      startDate = endDate - 1;
    };

    setLoadDate(
      {
        "loadDateStart":loadDateStart, 
        "loadDateEnd":loadDateEnd,
      },
    )

    // Any parameters in the query must be forced set to both defaults and current
    // filterBarValues

    const set_dict_values = (d) => {
      d["startDate"] = startDate;
      d["endDate"] = endDate;

      let gamesCheckedQuery = getParameterFromHash(url.hash, "gamesChecked");
      if (gamesCheckedQuery) {
        var gamesCheckedQuerySplit = gamesCheckedQuery.split(",");
        d['gamesChecked'] = gamesCheckedQuerySplit.map((x)=>parseInt(x));
      }
      
      let timeGranularityQuery = getParameterFromHash(url.hash, "timeGranularity");
      if(timeGranularityQuery!==null) d['timeGranularity'] = parseInt(timeGranularityQuery);

      let accountAgeRangeDaysQuery = getParameterFromHash(url.hash, "accountAgeRangeDays");
      if(accountAgeRangeDaysQuery!==null) d['accountAgeRangeDays'] = accountAgeRangeDaysQuery.split(",").map((x)=>parseInt(x));

      let mapTypeAvailableCheckedQuery = getParameterFromHash(url.hash, "mapTypeAvailableChecked");
      if(mapTypeAvailableCheckedQuery!==null) d['mapTypeAvailableChecked'] = mapTypeAvailableCheckedQuery.split(",").map((x)=>x==="true");

      let mapSizeQuery = getParameterFromHash(url.hash, "mapSize");
      if(mapSizeQuery!==null) d['mapSize'] = mapSizeQuery.split(",").map((x)=>parseInt(x));

      let gameTypesCheckedQuery = getParameterFromHash(url.hash, "gameTypesChecked");
      if(gameTypesCheckedQuery!==null) d['gameTypesChecked'] = gameTypesCheckedQuery.split(",").map((x)=>x==="true");

      let playerIdQuery = getParameterFromHash(url.hash, "playerId");
      if(playerIdQuery!==null) d['playerId'] = parseInt(playerIdQuery);

      return d;
    }

    setFilterBarInternalValuesDefault(set_dict_values({...filterBarInternalValuesDefault}));

    var newfilterBarInternalValues = [...filterBarInternalValues];
    for(var idx=0;idx<newfilterBarInternalValues.length;idx++) {
      newfilterBarInternalValues[idx] = set_dict_values({...newfilterBarInternalValues[idx]});
    }
    setFilterBarInternalValues(newfilterBarInternalValues)

    var newfilterBarValues = [...filterBarValues];
    for(var idx=0;idx<newfilterBarValues.length;idx++) {
      newfilterBarValues[idx] = set_dict_values({...newfilterBarValues[idx]});
    }
    setFilterBarValues(newfilterBarValues)

    // When the app is accessed from one of the child pages, such as:
    // release2022-07-17-01#/playerAward?startDate=2020-05-10 , the 
    // baseURI will contain "playerAward" which should not be included
    // in the path to the data. So we look for the string 
    // process.env.PUBLIC_URL and delete anything that is after

    var base_url;
    const index_release = document.baseURI.lastIndexOf(process.env.PUBLIC_URL);
    const index_slash = document.baseURI.substring(index_release+1).lastIndexOf("/");
    if(index_slash!=-1) {
      base_url = document.baseURI.substring(0, index_release+index_slash+1)
    } else {
      base_url = document.baseURI
    }

    if(process.env.NODE_ENV=='production') {
      base_url = base_url.replace(process.env.PUBLIC_URL,"");
    }

    instance.postMessage({
      url: base_url,
      env: process.env,
      date_start_ms: loadDateStart,
      date_end_ms: loadDateEnd,
    });
  }, [])

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerState(open);
  };

  const get_url_for_data = (months, old) => {
    let startDate = new Date((new Date()).valueOf() - months*30*24*3600*1000).valueOf();
    // TODO: replace previous loadStartDate
    return "#/gameModePlayed?loadDateStart=" + startDate + old;
  };

  const listDrawer = () => (
    <Box
      sx={{ width: 400 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        <ListItem
            secondaryAction={
              <IconButton edge="end" aria-label="delete" onClick={() => setModalOpenGamesModesPlayed(true)}>
                <HelpOutlineIcon />
              </IconButton>
            }
            component={RouterLink} to={{pathname:"gameModePlayed", search: addressFilter}} button key={"games_modes_played"}>
          <ListItemText primary={"Games Modes Played"}/>
        </ListItem>
        <ListItem component={RouterLink} to="/player_intake_lost" button key={"player_intake_lost"} >
          <ListItemText primary={"Players Gain/Lost"}/>
        </ListItem>
        <ListItem component={RouterLink} to={{pathname:"playerResign", search: addressFilter}} button key={"player_resign"} >
          <ListItemText primary={"Player Resign"}/>
        </ListItem>
        <ListItem component={RouterLink} to={{pathname:"playerAward", search: addressFilter}} button key={"player_award"} >
          <ListItemText primary={"Players Award"}/>
        </ListItem>
        <ListItem component={RouterLink} to="/playerAwardName" button key={"player_award_name"} >
          <ListItemText primary={"Players Award Name"} primaryTypographyProps={{color: 'LightGrey',}}/>
        </ListItem>
        <ListItem component={RouterLink} to="/playerCorrelation" button key={"player_correlation"} >
          <ListItemText primary={"Players Correlation"}/>
        </ListItem>

      </List>
    </Box>
  );

  const handleFilterBarValuesAdd = (t) => {
    setFilterBarValues([...filterBarValues].concat([{}]));
  };

  const handleFilterBarValuesUpdate = (index, updatedValues) => {
    var newFilterBarValues = [...filterBarValues];
    newFilterBarValues[index] = updatedValues;
    setFilterBarValues(newFilterBarValues)
  }

  return (
    <HashRouter>
      <div className="App">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AppBar position="static">
            <Toolbar>
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2 }}
                onClick={toggleDrawer(true)}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="left"
                open={drawerState}
                onClose={toggleDrawer(false)}
              >
                {listDrawer()}
              </Drawer>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                ZK Local Analysis (BETA)
              </Typography>
              <Typography variant="h5" component="div">
                Loaded battles: {(incrementalData!=null && 'battle_player' in incrementalData) ? incrementalData['battle_player']['battle_id'].length:0}
                ({incrementalData?(incrementalData.incrementalDataPercentage!==100?"~":""):""}{incrementalData?incrementalData.incrementalDataPercentage:0}%)
                {incrementalData &&
                  <Typography component="div">Data loaded from {incrementalData.last_date} until {incrementalData.first_date}.</Typography>
                }
                {incrementalData && incrementalData.incrementalDataPercentage && incrementalData.incrementalDataPercentage!=="100.00" &&
                    <Typography component="div">Data keeps loading in background...</Typography>
                }
                {incrementalData && incrementalData.incrementalDataPercentage && incrementalData.incrementalDataPercentage==="100.00" &&
                    <Typography component="div">(click setting icon for other options)</Typography>
                }
              </Typography>
              <div>
                <IconButton
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={() => setModalOpen(true)}
                  color="inherit"
                >
                  <HelpOutlineIcon style={{ fontSize: 48 }}/>
                </IconButton>
              </div>
              <div>
                <IconButton
                  aria-label="settings"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={() => setModalSettingsOpen(true)}
                  color="inherit"
                >
                  <SettingsOutlinedIcon style={{ fontSize: 48 }}/>
                </IconButton>
              </div>
             </Toolbar>
          </AppBar>
          <Modal
            open={modalOpen}
            onClose={()=>setModalOpen(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                ZK Local Analysis description
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                The end goal of this is to allow better understanding of what happens
                in ZK without requiring either direct access to database or maintining
                a server.
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                The crazy way to do this is to: scrap all information from the website
                (if people/admins/dev like the whole idea it might be more efficient to
                use a regular database dump, but before having that discussion I prefer
                to check how it looks); load all the information in your browser and
                do (locally) whatever you want with it - in this case visualize some
                aspects.
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                All the information can be downloaded as csv files (add /public to
                the link to get it). The data contains exclusively id-s (so no
                battle name, user names, etc.) to avoid any discussions like "is
                this private data", etc.
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                The code will probably be at some point open source, but until
                I do a couple of more visualizations don't feel like putting the
                effort to "clean it", "document it", etc.
              </Typography>
            </Box>
          </Modal>
          <Modal
            open={modalOpenGamesModesPlayed}
            onClose={()=>setModalOpenGamesModesPlayed(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Games Modes Played description
              </Typography>test
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                Allows to inspect how much time is spent per players in various mods, maps, etc. <br/>
                Any single player game (like tutorials) are not included here. <br/>
                Due to the way data is obtained (via scrapping) and how ZK pages report the time of
                the battle, for battled older than December 2021 the start time is approximate.

              </Typography>
            </Box>
          </Modal>
          <Modal
            open={modalSettingsOpen}
            onClose={()=>setModalSettingsOpen(false)}
            aria-labelledby="modal-settings-title"
            aria-describedby="modal-settings-description"
          >
            <Box sx={style}>
              <Typography id="modal-settings-title" variant="h6" component="h2">
                ZK Local Analysis settings
              </Typography>
              <Typography id="modal-settings-description" sx={{ mt: 2, mb: 2 }}>
                By default the last 3 months will be loaded, starting from
                yesterday. It is possible to select larger periods of time, but
                beware that will use lots of memory and take much longer. Note:
                page will be reloaded.
              </Typography>
              <Stack direction="column" spacing={1}>
                <Button onClick={e => { window.location.href=get_url_for_data(6, addressFilter); window.location.reload(); }} variant="outlined">
                  Last 6 months
                </Button>
                <Button onClick={e => { window.location.href=get_url_for_data(12, addressFilter); window.location.reload(); }} variant="outlined">
                  Last year
                </Button>
                <Button onClick={e => { window.location.href=get_url_for_data(3*12, addressFilter); window.location.reload(); }} variant="outlined">
                  Last 3 years
                </Button>
                <Button onClick={e => setModalWarningAllOpen(true)} variant="outlined">
                  All available data
                </Button>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                  <Typography >
                    Custom
                  </Typography>
                  <DateRange
                    closeIcon={true}
                    changed={changedDateRange}
                    startDate={startDate}
                    endDate={endDate}
                    startDateChanged={(newValue) => { setStartDate(newValue); setChangedDateRange(true); } }
                    endDateChanged={(newValue) => { setEndDate(newValue); setChangedDateRange(true); } }
                  />
                  <Button onClick={e => {
                    // DesktopDatePicker  can be one of normal javascript Date or 
                    // whatever DateRangePicker returns. If user changes only
                    // one (startDate or endDate) the other might stay the
                    // original value (which is Date)
                    window.location.href='#/gameModePlayed?startDate='+(new Date(startDate).valueOf())+'&endDate='+(new Date(endDate).valueOf()+ addressFilter);
                    window.location.reload();
                  } } variant="outlined">
                    Apply custom
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Modal>
          <Modal
            open={modalWarningAllOpen}
            onClose={()=>setModalWarningAllOpen(false)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                WARNING! Loading all data requires a lot of resources (memory
                and bandwidth). We recommand to do it only if you have more
                than 8 GB of RAM. Continue at your own risk!
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Button onClick={e => setModalWarningAllOpen(false)} variant="outlined">
                  Changed my mind...
                </Button>              
                <Button onClick={e => {
                    window.location.href='#/gameModePlayed?startDate='+(new Date('2010-01-01')).valueOf()+addressFilter;
                    window.location.reload();
                  }
                } variant="outlined">
                  I can take it...
                </Button>              
              </Stack>
            </Box>
          </Modal>
          {enoughDataLoaded && incrementalData && incrementalData.incrementalDataPercentage && incrementalData.incrementalDataPercentage!=="100.00" &&
              <Box sx={{ flexGrow: 1 }}>
                <Typography> (continuing data loading in the background, interface updates automatically)</Typography>
                <LinearProgress />
              </Box>
          }
          {!enoughDataLoaded &&
            <Box sx={{ flexGrow: 1 }}>
              <Typography>
              {loadingMessage}
              </Typography>
              {incrementalData && !('error' in incrementalData) &&
                <div>
                <Typography>
                {incrementalData?incrementalData.incrementalDataPercentage:0}% data loaded{incrementalData?",until date "+incrementalData.last_date:""}
                (once enough data is loaded you can use the interface, data will continue uploading in the background)
                </Typography>
                <LinearProgress variant="determinate" value={incrementalData?parseFloat(incrementalData.incrementalDataPercentage):0} />
                </div>
              }
            </Box>
          }
          {enoughDataLoaded &&
            <Routes>
              <Route exact path="/" element={<Navigate to="/gameModePlayed"/>}>                
              </Route>
              <Route path="/gameModePlayed" element={
                <Stack spacing={3}>
                  {filterBarValues.map( (elem, index) => {
                      return (
                        <Item key={index}>
                          <GameModePlayed
                            key={index}
                            jsonBattles={incrementalData['battles']}
                            jsonBattlePlayer={incrementalData['battle_player']}
                            jsonGames={incrementalData['games']}
                            jsonMaps={incrementalData['maps']}
                            jsonPlayers={incrementalData['players']}
                            filterBarValues={elem}
                          >
                            <FilterBar
                              jsonBattles={incrementalData['battles']}
                              jsonBattlePlayer={incrementalData['battle_player']}
                              jsonGames={incrementalData['games']}
                              jsonMaps={incrementalData['maps']}
                              jsonPlayers={incrementalData['players']}
                              hideGameModeType={true}
                              hidePlayerSelect={true}
                              indexState={index}
                              updateCallback={x=>handleFilterBarValuesUpdate(index,x)}
                            />
                          </GameModePlayed>
                        </Item>
                      );
                    })}
                    <Item>
                      <Button
                        variant="contained"
                        onClick={x=>handleFilterBarValuesAdd()}
                      >
                        Add new
                    </Button>
                  </Item>
                </Stack>
              } />
              <Route path="/player_intake_lost" element={
                <Stack spacing={3}>
                  {filterBarValues.map( (elem, index) => {
                      return (
                        <Item key={index}>
                          <PlayerIntakeLost
                            key={index}
                            jsonAwards={incrementalData['awards']}
                            jsonBattles={incrementalData['battles']}
                            jsonBattlePlayer={incrementalData['battle_player']}
                            jsonGames={incrementalData['games']}
                            jsonMaps={incrementalData['maps']}
                            jsonPlayers={incrementalData['players']}
                            filterBarValues={elem}
                          >
                            <FilterBar
                              jsonBattles={incrementalData['battles']}
                              jsonBattlePlayer={incrementalData['battle_player']}
                              jsonGames={incrementalData['games']}
                              jsonMaps={incrementalData['maps']}
                              jsonPlayers={incrementalData['players']}
                              indexState={index}
                              updateCallback={x=>handleFilterBarValuesUpdate(index,x)}
                            />
                          </PlayerIntakeLost>
                        </Item>
                      );
                    })}
                    <Item>
                      <Button
                        variant="contained"
                        onClick={x=>handleFilterBarValuesAdd()}
                      >
                        Add new
                    </Button>
                  </Item>
                </Stack>
              } />      
              <Route path="/playerResign" element={
                <Stack spacing={3}>
                  {filterBarValues.map( (elem, index) => {
                      return (
                        <Item key={index}>
                          <PlayerResign
                            key={index}
                            jsonAwards={incrementalData['awards']}
                            jsonBattles={incrementalData['battles']}
                            jsonBattlePlayer={incrementalData['battle_player']}
                            jsonGames={incrementalData['games']}
                            jsonMaps={incrementalData['maps']}
                            jsonPlayers={incrementalData['players']}
                            filterBarValues={elem}
                          >
                            <FilterBar
                              jsonBattles={incrementalData['battles']}
                              jsonBattlePlayer={incrementalData['battle_player']}
                              jsonGames={incrementalData['games']}
                              jsonMaps={incrementalData['maps']}
                              jsonPlayers={incrementalData['players']}
                              hideAccountAge={true}
                              hideTimeGranularity={true}
                              indexState={index}
                              updateCallback={x=>handleFilterBarValuesUpdate(index,x)}
                            />
                          </PlayerResign>
                        </Item>
                      );
                    })}
                    <Item>
                      <Button
                        variant="contained"
                        onClick={x=>handleFilterBarValuesAdd()}
                      >
                        Add new
                    </Button>
                  </Item>
                </Stack>
              } />
              <Route path="/playerAwardName" element={
                <Typography>Under construction</Typography>
                // <Stack spacing={3}>
                //   {menuSelected.playerAwardName["graphs"].map( (elem,index) => {
                //       return (
                //         <Item key={index}>
                //           <PlayerAwardName
                //             key={index}
                //             jsonAwards={incrementalData['awards']}
                //             jsonBattles={incrementalData['battles']}
                //             jsonBattlePlayer={incrementalData['battle_player']}
                //             jsonGames={incrementalData['games']}
                //             jsonMaps={incrementalData['maps']}
                //             jsonPlayers={incrementalData['players']}
                //           />
                //         </Item>
                //       );
                //     })}
                //     <Item>
                //       <Button
                //         variant="contained"
                //         onClick={x=>handleAddNew("playerAward")}
                //       >
                //         Add new
                //     </Button>
                //   </Item>
                // </Stack>
              } />
              <Route path="/playerAward" element={
                <Stack spacing={3}>
                  {filterBarValues.map( (elem, index) => {
                      return (
                        <Item key={index}>
                          <PlayerAward
                            key={index}
                            jsonAwards={incrementalData['awards']}
                            jsonBattles={incrementalData['battles']}
                            jsonBattlePlayer={incrementalData['battle_player']}
                            jsonGames={incrementalData['games']}
                            jsonMaps={incrementalData['maps']}
                            jsonPlayers={incrementalData['players']}
                            filterBarValues={elem}
                          >
                            <FilterBar
                              jsonBattles={incrementalData['battles']}
                              jsonBattlePlayer={incrementalData['battle_player']}
                              jsonGames={incrementalData['games']}
                              jsonMaps={incrementalData['maps']}
                              jsonPlayers={incrementalData['players']}
                              hideAccountAge={true}
                              hideTimeGranularity={true}
                              indexState={index}
                              updateCallback={x=>handleFilterBarValuesUpdate(index,x)}
                            />
                          </PlayerAward>
                        </Item>
                      );
                    })}
                    <Item>
                      <Button
                        variant="contained"
                        onClick={x=>handleFilterBarValuesAdd()}
                      >
                        Add new
                    </Button>
                  </Item>
                </Stack>
              } />
              <Route path="/playerCorrelation" element={
                <Stack spacing={3}>
                  {filterBarValues.map( (elem, index) => {
                      return (
                        <Item key={index}>
                          <PlayerCorrelation
                            key={index}
                            jsonBattles={incrementalData['battles']}
                            jsonBattlePlayer={incrementalData['battle_player']}
                            jsonGames={incrementalData['games']}
                            jsonMaps={incrementalData['maps']}
                            jsonPlayers={incrementalData['players']}
                            filterBarValues={elem}
                          >
                            <FilterBar
                              jsonBattles={incrementalData['battles']}
                              jsonBattlePlayer={incrementalData['battle_player']}
                              jsonGames={incrementalData['games']}
                              jsonMaps={incrementalData['maps']}
                              jsonPlayers={incrementalData['players']}
                              hideAccountAge={true}
                              hideTimeGranularity={true}
                              indexState={index}
                              updateCallback={x=>handleFilterBarValuesUpdate(index,x)}
                            />
                          </PlayerCorrelation>
                        </Item>
                      );
                    })}
                    <Item>
                      <Button
                        variant="contained"
                        onClick={x=>handleFilterBarValuesAdd()}
                      >
                        Add new
                    </Button>
                  </Item>
                </Stack>
              } />
            </Routes>
          }
        </LocalizationProvider>

      </div>
    </HashRouter>
  );
}

export default App;
